import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import LabelService from "service/LabelService";

export const getLabel = createAsyncThunk("getLabel", async (shopDomain) => {
  const response = await LabelService.getAllLabel(shopDomain);
  return response.data;
});

export const updateLabel = createAsyncThunk("updateLabel", async ({ id, data }) => {
  const response = await LabelService.updateLabel(id, data);
  return response.data;
});

// `condition.section` lists the enabled section rules: product, collection, include_section,
// ignore_section. Empty means no section filter at all. The two selector fields are plain
// comma-separated CSS selector strings, not lists.
const normalizeSection = (condition) => ({
  ...condition,
  section: Array.isArray(condition?.section) ? condition.section : [],
  include_sections: condition?.include_sections ?? "",
  ignore_sections: condition?.ignore_sections ?? "",
});

const checkSizeSetting = (state, object) => {
  if (
    state[`${object}`][`${object}_width_product`] ||
    state[`${object}`][`${object}_height_product`] ||
    state[`${object}`][`${object}_width_mobile`] ||
    state[`${object}`][`${object}_height_mobile`] ||
    state[`${object}`][`${object}_width_product_mobile`] ||
    state[`${object}`][`${object}_height_product_mobile`] ||
    state.design.font_size_product ||
    (state.design.font_size_mobile && state.design.font_size_mobile !== state.design.font_size) ||
    state.design.font_size_product_mobile
  ) {
    state.sizeMode = "custom";
  } else {
    state.sizeMode = "same";
  }
};

export const labelSlice = createSlice({
  name: "labels",
  initialState: {
    label: {},
    design: {},
    list: [],
    sizeMode: "same",
    openEditModal: false,
  },
  reducers: {
    setEditData: (state, action) => {
      const { design, ...labelData } = action.payload;
      Object.assign(state.label, labelData);
      if (design) Object.assign(state.design, design);
      checkSizeSetting(state, "label");
    },
    setLabelAttribute: (state, action) => {
      Object.assign(state.label, action.payload);
    },
    setConditionAttribute: (state, action) => {
      state.label.conditions = { ...state.label.conditions, ...action.payload };
    },
    setDesignAttribute: (state, action) => {
      Object.assign(state.design, action.payload);
    },
    setSizeMode: (state, action) => {
      state.sizeMode = action.payload;
    },
    toggleOpenEditModal: (state, action) => {
      state.openEditModal = !state.openEditModal;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getLabel.fulfilled, (state, action) => {
      state.list = action.payload.data;
    });
    builder.addCase(updateLabel.fulfilled, (state, action) => {
      const updated = action.payload?.data ?? action.payload;
      if (!updated?.id) return;
      const index = state.list.findIndex((label) => label.id === updated.id);
      if (index !== -1) {
        state.list[index] = updated;
      }
      const { design, ...labelData } = updated;
      // Replace instead of merge: the response is the source of truth, so a field the
      // API dropped or changed must not keep the value we optimistically sent.
      state.label = {
        ...labelData,
        // No section data in the response means "All sections".
        conditions: normalizeSection(labelData.conditions),
      };
      if (design) state.design = design;
      checkSizeSetting(state, "label");
    });
  },
});

export const {
  setEditData,
  setLabelAttribute,
  setConditionAttribute,
  setDesignAttribute,
  setSizeMode,
  toggleOpenEditModal,
} = labelSlice.actions;

export default labelSlice.reducer;
